import { apiClient } from './api';

export type DuplicateScanStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
export type DuplicateScanMode = 'cross_campaign' | 'same_campaign' | 'both';
export type DuplicateClusterAction = 'keep_one' | 'mark_dnc' | 'delete';

export interface DuplicateLeadScan {
  id: string;
  status: DuplicateScanStatus;
  progressPercent: number;
  totalPhonesScanned: number;
  totalClusters: number;
  totalDuplicateLeads: number;
  initialTotalClusters: number;
  initialTotalDuplicateLeads: number;
  scanMode: DuplicateScanMode;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DuplicateClusterMember {
  leadId: string;
  campaignId: string;
  campaignName: string;
  phoneE164: string;
  fullName: string;
  status: string;
  leadCreatedAt: string | null;
  isOnLiveCall: boolean;
  campaignIsActive: boolean;
  campaignStatus: string;
}

export interface DuplicateCluster {
  id: string;
  sharedPhones: string[];
  campaignCount: number;
  leadCount: number;
  members: DuplicateClusterMember[];
}

export type DuplicateBulkActionStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
export type DuplicateBulkKeeperStrategy = 'oldest_lead' | 'newest_lead';
export type DuplicateBulkScope = 'all' | 'selected';

export interface DuplicateBulkAction {
  id: string;
  scanId: string;
  action: DuplicateClusterAction;
  config: {
    scope: DuplicateBulkScope;
    clusterIds?: string[];
    keeperStrategy?: DuplicateBulkKeeperStrategy;
    targetCampaignIds?: string[];
  };
  status: DuplicateBulkActionStatus;
  progressPercent: number;
  totalClusters: number;
  processedClusters: number;
  affectedLeads: number;
  liveCallLeads: number;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScanCampaignOption {
  campaignId: string;
  campaignName: string;
  leadCount: number;
}

export interface DuplicateResolutionAffectedLead {
  leadId: string;
  campaignId: string;
  campaignName: string;
  fullName: string;
  phoneE164: string;
}

export interface DuplicateResolutionLog {
  id: string;
  scanId: string;
  bulkActionId: string | null;
  action: DuplicateClusterAction;
  source: 'single' | 'bulk';
  clusterId: string | null;
  sharedPhones: string[];
  keeperLeadId: string | null;
  keeperCampaignName: string;
  keeperFullName: string;
  keeperPhoneE164: string;
  affectedCount: number;
  affectedLeads: DuplicateResolutionAffectedLead[];
  createdAt: string;
}

export interface DuplicateActionLogSummary {
  totalLogEntries: number;
  totalLeadsAffected: number;
  totalLeadsKept: number;
  bulkActionCount: number;
}

export const duplicateLeadsService = {
  async startScan(campaignIds?: string[], scanMode: DuplicateScanMode = 'both'): Promise<DuplicateLeadScan> {
    const response = await apiClient.post<{ scan: DuplicateLeadScan }>('/duplicate-leads/scans', {
      campaignIds,
      scanMode,
    });
    return response.data.data.scan;
  },

  async listScans(): Promise<DuplicateLeadScan[]> {
    const response = await apiClient.get<{ scans: DuplicateLeadScan[] }>('/duplicate-leads/scans', {
      skipErrorToast: true,
    });
    return response.data.data.scans;
  },

  async getScan(scanId: string): Promise<DuplicateLeadScan> {
    const response = await apiClient.get<{ scan: DuplicateLeadScan }>(`/duplicate-leads/scans/${scanId}`);
    return response.data.data.scan;
  },

  async cancelScan(scanId: string): Promise<DuplicateLeadScan> {
    const response = await apiClient.post<{ scan: DuplicateLeadScan }>(`/duplicate-leads/scans/${scanId}/cancel`);
    return response.data.data.scan;
  },

  async getClusters(
    scanId: string,
    page = 1,
    limit = 25
  ): Promise<{
    clusters: DuplicateCluster[];
    pagination: { page: string; limit: string; total: string; totalPages: string };
  }> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const response = await apiClient.get<{
      clusters: DuplicateCluster[];
      pagination: { page: string; limit: string; total: string; totalPages: string };
    }>(`/duplicate-leads/scans/${scanId}/clusters?${params.toString()}`);
    return response.data.data;
  },

  async applyClusterAction(
    scanId: string,
    clusterId: string,
    action: DuplicateClusterAction,
    options?: { keeperLeadId?: string; targetCampaignIds?: string[] }
  ): Promise<{
    affected: number;
    liveCallLeadIds: string[];
    affectedCampaigns: Array<{ campaignId: string; campaignName: string; leadCount: number }>;
    targetLeadIds: string[];
  }> {
    const response = await apiClient.post<{
      affected: number;
      liveCallLeadIds: string[];
      affectedCampaigns: Array<{ campaignId: string; campaignName: string; leadCount: number }>;
      targetLeadIds: string[];
    }>(`/duplicate-leads/scans/${scanId}/clusters/${clusterId}/action`, {
      action,
      keeperLeadId: options?.keeperLeadId,
      targetCampaignIds: options?.targetCampaignIds,
    });
    return response.data.data;
  },

  getBulkStreamUrl(bulkActionId: string): string {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const fullUrl = baseUrl.startsWith('http')
      ? `${baseUrl}/duplicate-leads/bulk-actions/${bulkActionId}/stream`
      : `${window.location.origin}${baseUrl}/duplicate-leads/bulk-actions/${bulkActionId}/stream`;
    const url = new URL(fullUrl);
    if (token) url.searchParams.set('token', token);
    return url.toString();
  },

  async getScanCampaigns(scanId: string): Promise<ScanCampaignOption[]> {
    const response = await apiClient.get<{ campaigns: ScanCampaignOption[] }>(
      `/duplicate-leads/scans/${scanId}/campaigns`
    );
    return response.data.data.campaigns;
  },

  async startBulkAction(
    scanId: string,
    payload: {
      action: DuplicateClusterAction;
      scope?: DuplicateBulkScope;
      keeperStrategy?: DuplicateBulkKeeperStrategy;
      targetCampaignIds?: string[];
      clusterIds?: string[];
    }
  ): Promise<DuplicateBulkAction> {
    const response = await apiClient.post<{ bulkAction: DuplicateBulkAction }>(
      `/duplicate-leads/scans/${scanId}/bulk-actions`,
      payload
    );
    return response.data.data.bulkAction;
  },

  async listBulkActions(scanId: string, limit = 10): Promise<DuplicateBulkAction[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    const response = await apiClient.get<{ bulkActions: DuplicateBulkAction[] }>(
      `/duplicate-leads/scans/${scanId}/bulk-actions?${params.toString()}`,
      { skipErrorToast: true }
    );
    return response.data.data.bulkActions;
  },

  async getActionLogSummary(scanId: string): Promise<DuplicateActionLogSummary> {
    const response = await apiClient.get<{ summary: DuplicateActionLogSummary }>(
      `/duplicate-leads/scans/${scanId}/action-logs/summary`
    );
    return response.data.data.summary;
  },

  async getActionLogs(
    scanId: string,
    page = 1,
    limit = 25
  ): Promise<{
    logs: DuplicateResolutionLog[];
    pagination: { page: string; limit: string; total: string; totalPages: string };
  }> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const response = await apiClient.get<{
      logs: DuplicateResolutionLog[];
      pagination: { page: string; limit: string; total: string; totalPages: string };
    }>(`/duplicate-leads/scans/${scanId}/action-logs?${params.toString()}`);
    return response.data.data;
  },

  async exportActionLogs(scanId: string): Promise<void> {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const response = await fetch(`${baseUrl}/duplicate-leads/scans/${scanId}/action-logs/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `duplicate-action-logs-${scanId}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async cancelBulkAction(bulkActionId: string): Promise<DuplicateBulkAction> {
    const response = await apiClient.post<{ bulkAction: DuplicateBulkAction }>(
      `/duplicate-leads/bulk-actions/${bulkActionId}/cancel`
    );
    return response.data.data.bulkAction;
  },

  getScanStreamUrl(scanId: string): string {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const fullUrl = baseUrl.startsWith('http')
      ? `${baseUrl}/duplicate-leads/scans/${scanId}/stream`
      : `${window.location.origin}${baseUrl}/duplicate-leads/scans/${scanId}/stream`;
    const url = new URL(fullUrl);
    if (token) url.searchParams.set('token', token);
    return url.toString();
  },

  async exportScan(scanId: string): Promise<void> {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const response = await fetch(`${baseUrl}/duplicate-leads/scans/${scanId}/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `duplicate-leads-${scanId}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
